import { useAuthenticationStore } from "@stores/authentication";
import {
  type CcAttachment,
  type CcConversation,
  type CcConversationDetail,
  ccCreateConversation,
  ccDeleteUpload,
  CcError,
  ccGetConversation,
  ccListConversations,
  ccLogin,
  type CcMessage,
  type CcProfile,
  ccRate,
  ccSendMessage,
  ccSocketUrl,
  ccUpload,
  type CcUser,
  isCcConfigured,
} from "@utilities/customer-care";
import { io, type Socket } from "socket.io-client";
import { create } from "zustand";

/**
 * Customer Care chat state (spec: ai_docs/specs/customer-care-chat.md).
 *
 * - `status`: `idle` until the panel first opens; `connecting` while logging
 *   in; `ready`; `fallback` when the CC login answers 404 (server flag
 *   `ENABLE_CUSTOMER_CHAT_AUTH` off), or the request cannot reach the API at
 *   all (CORS, Cloudflare challenge, network), so the widget shows the legacy
 *   iframe;
 *   `conflict` (403 IDENTITY_CONFLICT) and `unavailable` (502 / network).
 * - One Socket.IO connection for the session; threads are rooms joined on
 *   open and left on close. The list has no customer-side realtime, so it is
 *   polled while the panel is open.
 * - The access token lives only here (memory); `reset()` on logout.
 */

export type CcStatus =
  | "idle"
  | "connecting"
  | "ready"
  | "fallback"
  | "conflict"
  | "unavailable";

export interface CcThread {
  messages: CcMessage[];
  senders: Record<string, CcUser>;
  loadedAt: string;
}

interface CustomerCareState {
  status: CcStatus;
  profile: CcProfile | null;
  conversations: CcConversation[];
  threads: Record<string, CcThread>;
  /** Conversation id → typing sender name and when it expires. */
  typing: Record<string, { name: string; until: number }>;
  /** The thread on screen: its messages never count as unread. */
  focusedId: string | null;
  socketConnected: boolean;
  /** Ratings given this session (the API has no read-back). */
  rated: Record<string, number>;
  lastListSync: string;
  /** Every sender seen so far (thread `senders`, socket events, agents):
   *  the list endpoint gives only ids, so cards look names up here. */
  users: Record<string, CcUser>;

  connect: () => Promise<void>;
  reset: () => void;
  refreshList: () => Promise<void>;
  openThread: (id: string) => Promise<void>;
  closeThread: (id: string) => void;
  setFocused: (id: string | null) => void;
  startConversation: (title?: string, firstMessage?: string) => Promise<string>;
  send: (
    id: string,
    text: string,
    attachments?: CcAttachment[],
  ) => Promise<void>;
  upload: (file: File) => Promise<string>;
  discardUpload: (url: string) => Promise<void>;
  rate: (id: string, stars: number) => Promise<void>;
  emitTyping: (id: string) => void;
}

interface TypingEvent {
  conversationId?: string | number;
  userId?: string | number;
  name?: string;
  firstName?: string;
}

/** Conversation ids whose detail was fetched for the list (once per session). */
const probed = new Set<string>();

let socket: Socket | null = null;
let presenceTimer: number | undefined;
let typingSentAt = 0;

const key = (id: string | number): string => String(id);
const nowIso = (): string => new Date().toISOString();

const sortByActivity = (list: CcConversation[]): CcConversation[] =>
  [...list].sort((a, b) => {
    const ta = Date.parse(a.lastMessage?.creationTime ?? a.creationTime);
    const tb = Date.parse(b.lastMessage?.creationTime ?? b.creationTime);
    return tb - ta;
  });

const mergeConversations = (
  current: CcConversation[],
  incoming: CcConversation[],
): CcConversation[] => {
  const byId = new Map(current.map(c => [key(c.id), c]));
  for (const c of incoming)
    byId.set(key(c.id), { ...byId.get(key(c.id)), ...c });
  return sortByActivity([...byId.values()]);
};

const rememberUsers = (
  current: Record<string, CcUser>,
  ...sources: Array<Record<string, CcUser> | CcUser | null | undefined>
): Record<string, CcUser> => {
  const users = { ...current };
  for (const source of sources) {
    if (source === null || source === undefined) continue;
    if (
      "id" in source &&
      (typeof source.id === "string" || typeof source.id === "number")
    ) {
      const user = source as CcUser;
      users[key(user.id)] = { ...users[key(user.id)], ...user };
    } else {
      for (const [id, user] of Object.entries(
        source as Record<string, CcUser>,
      )) {
        users[key(id)] = { ...users[key(id)], ...user };
      }
    }
  }
  return users;
};

/** A conversation detail as a list item: the same fields, no messages. */
const withoutMessages = (
  detail: CcConversation & { messages?: CcMessage[] },
): CcConversation => {
  const item: CcConversation & { messages?: CcMessage[] } = {
    ...detail,
    messages: undefined,
  };
  return item;
};

/** `typing` without the conversation's entry. */
const withoutTyping = (
  typing: CustomerCareState["typing"],
  id: string,
): CustomerCareState["typing"] =>
  Object.fromEntries(Object.entries(typing).filter(([other]) => other !== id));

const closeSocket = (): void => {
  window.clearInterval(presenceTimer);
  presenceTimer = undefined;
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
};

export const useCustomerCareStore = create<CustomerCareState>()((set, get) => {
  /** Runs `call` with the access token; on 401 logs in again once (no refresh token). */
  const withToken = async <T>(
    call: (token: string) => Promise<T>,
  ): Promise<T> => {
    const token = get().profile?.accessToken;
    if (token === undefined) throw new CcError(401, "NOT_LOGGED_IN");
    try {
      return await call(token);
    } catch (error) {
      if (!(error instanceof CcError) || error.status !== 401) throw error;
      const licenseToken = useAuthenticationStore.getState().token;
      const profile = await ccLogin(licenseToken);
      set({ profile });
      socket?.emit("authenticate", { token: profile.accessToken });
      return await call(profile.accessToken);
    }
  };

  const appendMessage = (message: CcMessage, sender?: CcUser): void => {
    const id = key(message.conversationId);
    set(state => {
      const thread: CcThread | undefined = state.threads[id];
      const isMine = key(message.userId) === key(state.profile?.userId ?? "");
      const threads =
        thread !== undefined
          ? {
              ...state.threads,
              [id]: {
                ...thread,
                messages: thread.messages.some(
                  m => key(m.id) === key(message.id),
                )
                  ? thread.messages.map(m =>
                      key(m.id) === key(message.id) ? message : m,
                    )
                  : [...thread.messages, message],
                senders:
                  sender !== undefined
                    ? { ...thread.senders, [key(sender.id)]: sender }
                    : thread.senders,
              },
            }
          : state.threads;
      const conversations = state.conversations.some(c => key(c.id) === id)
        ? sortByActivity(
            state.conversations.map(c =>
              key(c.id) === id
                ? {
                    ...c,
                    lastMessage: message,
                    unreadCount:
                      isMine || state.focusedId === id
                        ? 0
                        : (c.unreadCount ?? 0) + 1,
                  }
                : c,
            ),
          )
        : state.conversations;
      const typing = isMine ? state.typing : withoutTyping(state.typing, id);
      return {
        threads,
        conversations,
        typing,
        users:
          sender !== undefined
            ? rememberUsers(state.users, sender)
            : state.users,
      };
    });
  };

  const openSocket = (token: string): void => {
    if (socket !== null || ccSocketUrl() === "") return;
    socket = io(ccSocketUrl(), {
      auth: { token },
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => {
      set({ socketConnected: true });
      // Re-join the rooms of the threads that are open.
      for (const id of Object.keys(get().threads)) {
        socket?.emit("join:conversation", { conversationId: id });
      }
    });
    socket.on("disconnect", () => {
      set({ socketConnected: false });
    });
    socket.on("connect_error", () => {
      set({ socketConnected: false });
    });
    socket.on(
      "message:new",
      (event: {
        conversationId: string | number;
        message: CcMessage;
        sender?: CcUser;
      }) => {
        const message = {
          ...event.message,
          conversationId: event.message?.conversationId ?? event.conversationId,
        };
        appendMessage(message, event.sender);
        // A message in a conversation the list does not know yet: refresh it.
        if (
          !get().conversations.some(
            c => key(c.id) === key(message.conversationId),
          )
        ) {
          void get().refreshList();
        }
      },
    );
    socket.on(
      "message:updated",
      (event: {
        messageId: string | number;
        message: string;
        attachments?: CcAttachment[];
      }) => {
        set(state => {
          const threads = { ...state.threads };
          for (const id of Object.keys(threads)) {
            threads[id] = {
              ...threads[id],
              messages: threads[id].messages.map(m =>
                key(m.id) === key(event.messageId)
                  ? {
                      ...m,
                      message: event.message,
                      attachments: event.attachments ?? m.attachments,
                    }
                  : m,
              ),
            };
          }
          return { threads };
        });
      },
    );
    socket.on("message:deleted", (event: { messageId: string | number }) => {
      set(state => {
        const threads = { ...state.threads };
        for (const id of Object.keys(threads)) {
          threads[id] = {
            ...threads[id],
            messages: threads[id].messages.filter(
              m => key(m.id) !== key(event.messageId),
            ),
          };
        }
        return { threads };
      });
    });
    const onTyping = (event: TypingEvent, on: boolean): void => {
      if (event.conversationId === undefined) return;
      if (
        event.userId !== undefined &&
        key(event.userId) === key(get().profile?.userId ?? "")
      )
        return;
      const id = key(event.conversationId);
      set(state => {
        if (!on) return { typing: withoutTyping(state.typing, id) };
        const thread = state.threads[id];
        const sender =
          event.userId !== undefined
            ? thread?.senders[key(event.userId)]
            : undefined;
        return {
          typing: {
            ...state.typing,
            [id]: {
              name: event.name ?? event.firstName ?? sender?.firstName ?? "",
              until: Date.now() + 6000,
            },
          },
        };
      });
    };
    socket.on("typing:start", (event: TypingEvent) => {
      onTyping(event, true);
    });
    socket.on("typing:stop", (event: TypingEvent) => {
      onTyping(event, false);
    });
    presenceTimer = window.setInterval(() => {
      socket?.emit("presence:ping");
    }, 30000);
  };

  return {
    status: "idle",
    profile: null,
    conversations: [],
    threads: {},
    typing: {},
    focusedId: null,
    socketConnected: false,
    rated: {},
    lastListSync: "",
    users: {},

    connect: async () => {
      if (get().status === "connecting" || get().status === "ready") return;
      if (!isCcConfigured()) {
        set({ status: "fallback" });
        return;
      }
      set({ status: "connecting" });
      try {
        const licenseToken = useAuthenticationStore.getState().token;
        const profile = await ccLogin(licenseToken);
        set({ profile });
        const conversations = await ccListConversations(profile.accessToken);
        set({
          conversations: sortByActivity(conversations),
          lastListSync: nowIso(),
          status: "ready",
        });
        openSocket(profile.accessToken);
      } catch (error) {
        if (error instanceof CcError) {
          // 404: the server's customer-chat flag is off → legacy iframe.
          if (error.status === 404) {
            set({ status: "fallback" });
            return;
          }
          if (error.status === 403) {
            set({ status: "conflict" });
            return;
          }
        } else {
          // The request never reached the API (CORS, a Cloudflare challenge,
          // network): keep the legacy support board, as for the 404.
          console.error("[customer care] CC API unreachable:", error);
          set({ status: "fallback" });
          return;
        }
        console.error("[customer care] login failed:", error);
        set({ status: "unavailable" });
      }
    },

    reset: () => {
      closeSocket();
      probed.clear();
      set({
        status: "idle",
        profile: null,
        conversations: [],
        threads: {},
        typing: {},
        focusedId: null,
        socketConnected: false,
        rated: {},
        lastListSync: "",
        users: {},
      });
    },

    refreshList: async () => {
      if (get().status !== "ready") return;
      try {
        // The full list every time: `/conversations/new?since=` only lists
        // conversations created since, not ones whose last message changed,
        // which left the list stale until a socket event arrived.
        const incoming = await withToken(
          async t => await ccListConversations(t),
        );
        set(state => ({
          conversations: mergeConversations(state.conversations, incoming),
          lastListSync: nowIso(),
          users: rememberUsers(
            state.users,
            ...incoming.map(c => c.agent),
            ...incoming.map(c => c.user),
          ),
        }));
        // The list item does not identify who answered (no sender details,
        // and its lastMessage may not even carry the sender id): load each
        // conversation's detail once, in batches, so the cards read the
        // sender from the same `senders` map the thread uses.
        const { threads } = get();
        const pending = get()
          .conversations.map(c => key(c.id))
          .filter(id => threads[id] === undefined && !probed.has(id))
          .slice(0, 10);
        if (pending.length === 0) return;
        pending.forEach(id => probed.add(id));
        const details = await Promise.all(
          pending.map(
            async id =>
              await withToken(async t => await ccGetConversation(t, id)).catch(
                () => null,
              ),
          ),
        );
        set(state => {
          const nextThreads = { ...state.threads };
          details.forEach((detail, index) => {
            if (detail === null) return;
            const id = pending[index];
            if (nextThreads[id] !== undefined) return;
            nextThreads[id] = {
              messages: detail.messages ?? [],
              senders: detail.senders ?? {},
              loadedAt: nowIso(),
            };
          });
          return {
            threads: nextThreads,
            users: rememberUsers(
              state.users,
              ...details.map(d => d?.senders),
              ...details.map(d => d?.agent),
            ),
          };
        });
      } catch (error) {
        console.error("[customer care] list refresh failed:", error);
      }
    },

    openThread: async id => {
      const detail: CcConversationDetail = await withToken(
        async t => await ccGetConversation(t, id),
      );
      set(state => ({
        users: rememberUsers(state.users, detail.senders, detail.agent),
        threads: {
          ...state.threads,
          [id]: {
            messages: detail.messages ?? [],
            senders: detail.senders ?? {},
            loadedAt: nowIso(),
          },
        },
        conversations: state.conversations.some(c => key(c.id) === id)
          ? state.conversations.map(c =>
              key(c.id) === id
                ? withoutMessages({ ...c, ...detail, unreadCount: 0 })
                : c,
            )
          : sortByActivity([
              ...state.conversations,
              { ...detail, unreadCount: 0 },
            ]),
      }));
      socket?.emit("join:conversation", { conversationId: id });
    },

    closeThread: id => {
      socket?.emit("leave:conversation", { conversationId: id });
    },

    setFocused: id => {
      set(state => ({
        focusedId: id,
        conversations:
          id === null
            ? state.conversations
            : state.conversations.map(c =>
                key(c.id) === id ? { ...c, unreadCount: 0 } : c,
              ),
      }));
    },

    startConversation: async (title, firstMessage) => {
      // The title is what the list cards show: a topic, or the first line of
      // a typed first message, kept short. The API does not summarise.
      const shortTitle = (title ?? "").trim().split("\n")[0].slice(0, 80);
      const created = await withToken(
        async t =>
          await ccCreateConversation(
            t,
            shortTitle === "" ? undefined : shortTitle,
          ),
      );
      const id = key(created.id);
      await get().openThread(id);
      if (firstMessage !== undefined && firstMessage !== "") {
        await get().send(id, firstMessage);
      } else {
        void get().refreshList();
      }
      return id;
    },

    send: async (id, text, attachments = []) => {
      const message = await withToken(
        async t => await ccSendMessage(t, id, text, attachments),
      );
      appendMessage({
        ...message,
        conversationId: message.conversationId ?? id,
      });
    },

    upload: async file => {
      const result = await withToken(async t => await ccUpload(t, file));
      return result.url;
    },

    discardUpload: async url => {
      try {
        await withToken(async t => {
          await ccDeleteUpload(t, url);
        });
      } catch {
        // Best-effort cleanup.
      }
    },

    rate: async (id, stars) => {
      const agentId = get().conversations.find(c => key(c.id) === id)?.agentId;
      await withToken(async t => {
        await ccRate(t, id, stars, agentId);
      });
      set(state => ({ rated: { ...state.rated, [id]: stars } }));
    },

    emitTyping: id => {
      const now = Date.now();
      if (now - typingSentAt < 2000) return;
      typingSentAt = now;
      socket?.emit("typing", { conversationId: id });
    },
  };
});

/**
 * Thread polling: the full conversation, merged by message id, whether or not
 * the socket reports itself connected. A connected socket whose room never
 * delivers (proxy, CORS, a missed join) would otherwise leave the thread
 * frozen; `GET /conversations/:id` is the source of truth.
 */
export const pollThread = async (id: string): Promise<void> => {
  const state = useCustomerCareStore.getState();
  if (state.status !== "ready" || state.threads[id] === undefined) return;
  try {
    const token = state.profile?.accessToken ?? "";
    const detail = await ccGetConversation(token, id);
    useCustomerCareStore.setState(s => {
      const current = s.threads[id];
      if (current === undefined) return s;
      const known = new Set(current.messages.map(m => key(m.id)));
      const fresh = detail.messages ?? [];
      const added = fresh.filter(m => !known.has(key(m.id)));
      const conversations = s.conversations.map(c =>
        key(c.id) === id
          ? {
              ...c,
              ...detail,
              messages: undefined,
              lastMessage: fresh[fresh.length - 1] ?? c.lastMessage,
              unreadCount: s.focusedId === id ? 0 : c.unreadCount,
            }
          : c,
      ) as CcConversation[];
      const typing = added.some(
        m => key(m.userId) !== key(s.profile?.userId ?? ""),
      )
        ? withoutTyping(s.typing, id)
        : s.typing;
      return {
        conversations,
        typing,
        users: rememberUsers(s.users, detail.senders, detail.agent),
        threads: {
          ...s.threads,
          [id]: {
            ...current,
            messages:
              added.length > 0
                ? [...current.messages, ...added]
                : current.messages,
            senders: { ...current.senders, ...(detail.senders ?? {}) },
            loadedAt: nowIso(),
          },
        },
      };
    });
  } catch (error) {
    console.error("[customer care] thread poll failed:", error);
  }
};

/** Unread replies across every conversation, for the button badge. */
export const selectUnread = (state: CustomerCareState): number =>
  state.conversations.reduce((total, c) => total + (c.unreadCount ?? 0), 0);
